import { interpolateCopy } from '@/lib/interpolate-copy'

export type LegalDocumentId = 'privacy' | 'terms'

export type LegalSection = {
  heading: string
  body: string
}

export type LegalDocument = {
  title: string
  updated: string
  intro: string
  sections: LegalSection[]
}

/**
 * Platform default in-app legal text. `{brand}` is the tenant brand. Packs replace a document with
 * `signup.legal.documents.<id>` in `config/ui.json`, or link out with an absolute `termsUrl` / `privacyUrl`.
 */
export const LEGAL_DOCUMENTS: Record<LegalDocumentId, LegalDocument> = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'October 2026',
    intro:
      '{brand} ("we", "us") operates this service. This policy describes how we collect, use, and protect personal information when you use it.',
    sections: [
      {
        heading: 'Information we collect',
        body:
          'Account information (name, email, role), profile and organisation data you provide, content you create in the service, usage logs, and cookies or session data required to operate it. When you sign in with a third-party identity provider, we receive the identity information that provider permits (such as name and email).',
      },
      {
        heading: 'How we use information',
        body:
          'To provide sign-in and the features you use; to improve the product; and to comply with law.',
      },
      {
        heading: 'Third-party services',
        body:
          'We use identity providers, email delivery, hosting, and analytics providers. Their use of data is governed by their own policies.',
      },
      {
        heading: 'Retention and security',
        body:
          'We retain data while your account is active and as required for legal or operational purposes. We apply reasonable technical and organisational measures to protect data. No method of transmission over the Internet is fully secure.',
      },
      {
        heading: 'Your rights',
        body:
          'Depending on your location, you may have rights to access, correct, delete, or export personal data, or to object to certain processing. Contact us through the service to make a request.',
      },
      {
        heading: 'Changes',
        body: 'This policy may be updated; material changes will be reflected on this page.',
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    updated: 'October 2026',
    intro:
      'These Terms govern access to and use of {brand}. By creating an account or using the service, you agree to these Terms.',
    sections: [
      {
        heading: 'Use of the platform',
        body:
          'You must provide accurate information, keep credentials secure, and use {brand} only for lawful purposes. You are responsible for content you publish, including material shared to third-party networks.',
      },
      {
        heading: 'Third-party sign-in and sharing',
        body:
          'Optional third-party sign-in and sharing are subject to those providers\' terms as well.',
      },
      {
        heading: 'Intellectual property',
        body:
          '{brand} retains rights in the platform. You retain rights in your content; you grant us a licence to host and display it as needed to operate the service.',
      },
      {
        heading: 'Limitation of liability',
        body:
          'The platform is provided "as is" to the extent permitted by law. {brand} is not liable for indirect or consequential damages arising from use of the service or third-party integrations.',
      },
      {
        heading: 'Changes',
        body:
          'We may update these Terms; continued use after changes constitutes acceptance of the revised Terms.',
      },
    ],
  },
}

/** The tenant's document (pack override or platform default) with `{brand}` filled in. */
export function resolveLegalDocument(
  documentId: LegalDocumentId,
  brand: string,
  overrides?: Partial<Record<LegalDocumentId, LegalDocument>>,
): LegalDocument {
  const doc = overrides?.[documentId] ?? LEGAL_DOCUMENTS[documentId]
  const fill = (text: string) => interpolateCopy(text, { brand })
  return {
    title: fill(doc.title),
    updated: doc.updated,
    intro: fill(doc.intro),
    sections: doc.sections.map((section) => ({
      heading: fill(section.heading),
      body: fill(section.body),
    })),
  }
}
